/**
 * F-032 — Local top-10 leaderboard.
 *
 * Persists run records (score · merges · iso date) sorted descending by
 * score. Online leaderboard is deferred (ADR open) — interface stays
 * implementation-agnostic so a future Cloudflare/Supabase backend can
 * slot in.
 */

import {
  createMemoryStorage,
  type KVStorage,
} from "./high-score-store.ts";

const STORAGE_KEY = "cosmic-suika.leaderboard.v1";
const TOP_N = 10;

export interface LeaderboardEntry {
  score: number;
  merges: number;
  iso: string; // YYYY-MM-DD
}

export interface Leaderboard {
  list(): ReadonlyArray<LeaderboardEntry>;
  record(entry: LeaderboardEntry): { rank: number; placed: boolean };
  clear(): void;
}

export interface LeaderboardOptions {
  storage?: KVStorage;
  storageKey?: string;
  topN?: number;
}

function defaultStorage(): KVStorage {
  if (typeof window === "undefined") return createMemoryStorage();
  try {
    return window.localStorage;
  } catch {
    return createMemoryStorage();
  }
}

export function createLeaderboard(
  options: LeaderboardOptions = {},
): Leaderboard {
  const storage = options.storage ?? defaultStorage();
  const key = options.storageKey ?? STORAGE_KEY;
  const cap = options.topN ?? TOP_N;
  if (cap <= 0) {
    throw new Error("createLeaderboard: topN must be positive");
  }

  let entries: LeaderboardEntry[] = (() => {
    const raw = storage.getItem(key);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as LeaderboardEntry[];
      return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
    } catch {
      return [];
    }
  })();
  entries.sort((a, b) => b.score - a.score);

  const persist = (): void => {
    try {
      storage.setItem(key, JSON.stringify(entries));
    } catch {
      /* quota — silent */
    }
  };

  const record = (
    entry: LeaderboardEntry,
  ): { rank: number; placed: boolean } => {
    if (!isEntry(entry)) return { rank: -1, placed: false };
    const next = [...entries, entry].sort((a, b) => b.score - a.score);
    const trimmed = next.slice(0, cap);
    const rank = trimmed.findIndex((e) => e === entry);
    if (rank === -1) return { rank: -1, placed: false };
    entries = trimmed;
    persist();
    return { rank, placed: true };
  };

  const clear = (): void => {
    entries = [];
    persist();
  };

  return {
    list: () => [...entries],
    record,
    clear,
  };
}

function isEntry(value: unknown): value is LeaderboardEntry {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<LeaderboardEntry>;
  return (
    typeof v.score === "number" &&
    Number.isFinite(v.score) &&
    v.score >= 0 &&
    typeof v.merges === "number" &&
    typeof v.iso === "string"
  );
}

export const __testing = { STORAGE_KEY, TOP_N };
